import React, { useEffect, useMemo, useState } from 'react';
import { getRaceDrivers, getResults } from '../../api';

function normalizeDriverCode(code) {
    if (!code) return '';
    return String(code).trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function extractTop10Codes(prediction) {
    const parsed = prediction?.responseJson || (() => {
        if (!prediction?.finalResponse) return null;
        try {
            return JSON.parse(prediction.finalResponse);
        } catch (_err) {
            return null;
        }
    })();

    if (!parsed) return [];
    const top10 = Array.isArray(parsed.top10) ? parsed.top10 : [];
    return top10.map((item) => normalizeDriverCode(item?.driverCode || item?.code || item?.driver_code || item?.driver || '')).filter(Boolean);
}

export default function AiPredictionPerformanceChart({ raceId, predictions }) {
    const [raceResults, setRaceResults] = useState([]);
    const [raceDrivers, setRaceDrivers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;

        async function loadMetrics() {
            if (!raceId || !predictions?.length) {
                setRaceResults([]);
                setRaceDrivers([]);
                return;
            }

            setLoading(true);
            setError('');

            try {
                const [results, drivers] = await Promise.all([getResults(raceId), getRaceDrivers(raceId)]);
                if (cancelled) return;
                setRaceResults(Array.isArray(results) ? results : []);
                setRaceDrivers(Array.isArray(drivers) ? drivers : []);
            } catch (e) {
                if (cancelled) return;
                setError('Unable to load race result metrics');
                setRaceResults([]);
                setRaceDrivers([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadMetrics();
        return () => { cancelled = true; };
    }, [raceId, predictions.length]);

    const driverCodeById = useMemo(() => {
        const map = new Map();
        raceDrivers.forEach((driver) => {
            const id = Number(driver?.id);
            if (!Number.isFinite(id)) return;
            map.set(id, normalizeDriverCode(driver?.code || driver?.driver_code || driver?.name || ''));
        });
        return map;
    }, [raceDrivers]);

    const actualCodes = useMemo(() => {
        return (raceResults || []).map((driverId) => normalizeDriverCode(driverCodeById.get(Number(driverId)) || ''));
    }, [raceResults, driverCodeById]);

    const groups = useMemo(() => {
        const buckets = {};

        if (!predictions?.length) return [];

        predictions.forEach((prediction) => {
            const top10Codes = extractTop10Codes(prediction);
            if (!top10Codes.length) return;

            const runKey = prediction.runKey || prediction.runType || 'unknown';
            const key = String(runKey).trim() || 'unknown';
            const matched = top10Codes.filter((code) => code && actualCodes.includes(code)).length;
            const exact = top10Codes.reduce((sum, code, index) => (code && code === actualCodes[index] ? sum + 1 : sum), 0);
            const accuracy = actualCodes.length ? matched / Math.min(10, actualCodes.length) : 0;

            if (!buckets[key]) {
                buckets[key] = {
                    runKey: key,
                    count: 0,
                    totalAccuracy: 0,
                    totalExactMatches: 0,
                };
            }
            buckets[key].count += 1;
            buckets[key].totalAccuracy += accuracy;
            buckets[key].totalExactMatches += exact;
        });

        return Object.values(buckets)
            .map((bucket) => ({
                ...bucket,
                avgAccuracy: bucket.count ? bucket.totalAccuracy / bucket.count : 0,
                avgExactMatches: bucket.count ? bucket.totalExactMatches / bucket.count : 0,
            }))
            .sort((a, b) => b.avgAccuracy - a.avgAccuracy || b.avgExactMatches - a.avgExactMatches);
    }, [predictions, actualCodes]);

    return (
        <div className="card">
            <div className="card-body">
                <h5 className="card-title mb-3">AI Prediction Performance</h5>
                {loading && <div className="small text-muted mb-3">Loading performance metrics…</div>}
                {!raceId && <div className="small text-muted">Select a race to see prediction performance.</div>}
                {raceId && !loading && !predictions?.length && (
                    <div className="small text-muted">No successful predictions to chart for this race yet.</div>
                )}
                {raceId && !loading && predictions?.length > 0 && actualCodes.length === 0 && (
                    <div className="small text-muted">Race results are required to calculate prediction performance.</div>
                )}
                {raceId && !loading && error && <div className="alert alert-danger py-2">{error}</div>}
                {raceId && !loading && actualCodes.length > 0 && groups.length > 0 && (
                    <div>
                        <div className="mb-3 small text-muted">Averages are shown by prompt type/run key from successful AI predictions only.</div>
                        {groups.map((group) => (
                            <div key={group.runKey} className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <strong>{group.runKey}</strong>
                                    <span className="small text-muted">{group.count} run{group.count !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="progress" style={{ height: '1.35rem' }}>
                                    <div
                                        className="progress-bar"
                                        role="progressbar"
                                        style={{ width: `${Math.round(group.avgAccuracy * 100)}%` }}
                                        aria-valuenow={Math.round(group.avgAccuracy * 100)}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                    >
                                        {Math.round(group.avgAccuracy * 100)}%
                                    </div>
                                </div>
                                <div className="small text-muted mt-1">
                                    Exact position average: {group.avgExactMatches.toFixed(1)} / 10
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
